import React, { useRef } from 'react';
import { scoreToColor } from './areaData.js';
import { scoreScaleWords } from './scoreWords.js';
import './scoreFilters.css';

// Area-score range picker drawn as the map's own colour scale: one segment per half score (1–6),
// the chosen range in full colour, the rest faded. Drag a handle, tap a segment (moves the nearer
// handle) or use the arrow keys on a handle.
const SCORES = Array.from({ length: 11 }, (_, index) => 1 + index * 0.5);
const toIndex = (score) => Math.min(10, Math.max(0, Math.round((Number(score) - 1) * 2)));
const toScore = (index) => SCORES[index];

export const formatScoreRange = (range, swedish) => {
    const [low, high] = range;
    if (low <= 1 && high >= 6) return swedish ? 'Alla' : 'Any';
    const format = (value) => (swedish ? String(value).replace('.', ',') : String(value));
    return `${format(low)} – ${format(high)}`;
};

const ScoreRangeBar = ({ label, value, onChange, swedish, size = 'small', showScale = false, headExtra = null }) => {
    const barRef = useRef(null);
    const dragRef = useRef(null);
    const lowIndex = toIndex(value[0]);
    const highIndex = toIndex(value[1]);
    const isFull = lowIndex === 0 && highIndex === 10;

    const update = (nextLow, nextHigh) => {
        const low = Math.min(nextLow, nextHigh);
        const high = Math.max(nextLow, nextHigh);
        if (low === lowIndex && high === highIndex) return;
        onChange([toScore(low), toScore(high)]);
    };

    const indexAt = (clientX) => {
        const rect = barRef.current.getBoundingClientRect();
        const ratio = (clientX - rect.left) / Math.max(1, rect.width);
        return Math.min(10, Math.max(0, Math.floor(ratio * 11)));
    };

    const moveHandle = (handle, index) => {
        if (handle === 'low') update(Math.min(index, highIndex), highIndex);
        else update(lowIndex, Math.max(index, lowIndex));
    };

    const handlePointerDown = (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        const fromHandle = event.target?.dataset?.handle;
        // On touch screens only the handles drag: a finger on the colours is scrolling the page (pan-y).
        if (event.pointerType === 'touch' && !fromHandle) return;
        const index = indexAt(event.clientX);
        let handle = fromHandle;
        if (!handle) {
            // The nearer handle; past either end, that end's handle.
            if (index <= lowIndex) handle = 'low';
            else if (index >= highIndex) handle = 'high';
            else handle = index - lowIndex <= highIndex - index ? 'low' : 'high';
        }
        dragRef.current = handle;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        moveHandle(handle, index);
    };

    const handlePointerMove = (event) => {
        if (!dragRef.current) return;
        moveHandle(dragRef.current, indexAt(event.clientX));
    };

    const endDrag = () => {
        dragRef.current = null;
    };

    const handleKeyDown = (handle) => (event) => {
        const current = handle === 'low' ? lowIndex : highIndex;
        let next = null;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = current - 1;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = current + 1;
        else if (event.key === 'Home') next = handle === 'low' ? 0 : lowIndex;
        else if (event.key === 'End') next = handle === 'high' ? 10 : highIndex;
        if (next == null) return;
        event.preventDefault();
        next = Math.min(10, Math.max(0, next));
        if (handle === 'low') update(Math.min(next, highIndex), highIndex);
        else update(lowIndex, Math.max(next, lowIndex));
    };

    const handleLabel = (which) => (swedish
        ? `${label}, ${which === 'low' ? 'lägsta' : 'högsta'} betyg`
        : `${label}, ${which === 'low' ? 'lowest' : 'highest'} score`);

    return (
        <div className={`score-range score-range--${size}`}>
            <div className="score-range-head">
                <span className="score-range-label">{label}</span>
                <span className={`score-range-value${isFull ? ' is-full' : ''}`}>{formatScoreRange(value, swedish)}</span>
                {headExtra}
            </div>
            <div
                className="score-range-track"
                ref={barRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
            >
                <div className="score-range-segments" aria-hidden="true">
                    {SCORES.map((score, index) => (
                        <span
                            key={score}
                            className={`score-range-segment${index >= lowIndex && index <= highIndex ? '' : ' is-out'}`}
                            style={{ background: scoreToColor[score] }}
                        />
                    ))}
                </div>
                {['low', 'high'].map((handle) => {
                    const index = handle === 'low' ? lowIndex : highIndex;
                    const left = handle === 'low' ? (index / 11) * 100 : ((index + 1) / 11) * 100;
                    return (
                        <span
                            key={handle}
                            data-handle={handle}
                            className="score-range-handle"
                            style={{ left: `${left}%` }}
                            role="slider"
                            tabIndex={0}
                            aria-label={handleLabel(handle)}
                            aria-valuemin={1}
                            aria-valuemax={6}
                            aria-valuenow={toScore(index)}
                            onKeyDown={handleKeyDown(handle)}
                        />
                    );
                })}
            </div>
            {showScale && (
                <div className="score-range-scale">
                    {scoreScaleWords(swedish).map((label) => <span key={label}>{label}</span>)}
                </div>
            )}
        </div>
    );
};

export default ScoreRangeBar;
